/**
 * Nana's Wisdom Library
 *
 * Random intergenerational wisdom popups.
 * Nana appears randomly to offer perspective and comfort.
 *
 * Australian English throughout.
 */

export const NANA_VARIANTS = [
  { id: 'rose', name: 'Nana Rose', tagline: 'A bit of wisdom for you, dear', image: '/images/nana/nana-rose.png', description: 'White/European, silver hair, glasses, warm cardigan' },
  { id: 'mae', name: 'Nana Mae', tagline: 'Let me share something with you', image: '/images/nana/nana-mae.png', description: 'Black/African heritage, grey natural hair, bright colours' },
  { id: 'mei', name: 'Nana Mei', tagline: 'A thought from someone who\'s been there', image: '/images/nana/nana-mei.png', description: 'East Asian, salt-and-pepper hair, kind eyes' },
  { id: 'priya', name: 'Nana Priya', tagline: 'Some wisdom from an old friend', image: '/images/nana/nana-priya.png', description: 'South Asian, grey braid, traditional touches' },
  { id: 'aroha', name: 'Nana Aroha', tagline: 'Something to carry with you', image: '/images/nana/nana-aroha.png', description: 'Maori/Pacific, flowing grey hair' },
];

// Probability of showing Nana wisdom (0.0 to 1.0)
export const NANA_POPUP_PROBABILITY = 0.25; // 25% chance

// Minimum time between Nana popups (in milliseconds)
export const NANA_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours

export const NANA_WISDOM = [
  // ===== DIGITAL AGE PERSPECTIVE (1-10) =====
  {
    id: 'nana_001',
    text: "In my day, we didn't have 'read receipts' to stress about. But you know what we did have? The same anxiety about whether someone got our letter. Some things never change, love. Trust the friendship, not the technology.",
    category: 'digital',
    relatedModules: ['module-04'],
    mood: 'reassuring'
  },
  {
    id: 'nana_002',
    text: "Your generation sees everyone's highlight reel and thinks you're the only one struggling. In my day, we only knew what our neighbours were up to. Trust me — everyone was a mess behind closed doors then too. You're doing better than you think.",
    category: 'digital',
    relatedModules: ['module-07'],
    mood: 'validating'
  },
  {
    id: 'nana_003',
    text: "In my day, 'ghosting' meant something quite different. Usually involved a seance. But people disappeared from friendships back then too — they just called it 'drifting apart.' The pain isn't new, only the name for it.",
    category: 'digital',
    relatedModules: ['module-05'],
    mood: 'normalising'
  },
  {
    id: 'nana_004',
    text: "Some of my friends think online friendships aren't 'real.' What nonsense. I had a pen pal for forty years I never met in person. A connection is a connection, however it travels.",
    category: 'digital',
    relatedModules: ['module-06'],
    mood: 'validating'
  },
  {
    id: 'nana_005',
    text: "You're worried because they haven't replied in two hours? Love, I once waited three weeks for a letter to arrive. When it comes to friendship, sometimes no news is just... no news. Not a crisis.",
    category: 'digital',
    relatedModules: ['module-04'],
    mood: 'reassuring'
  },
  {
    id: 'nana_006',
    text: "In my day, we didn't know what we were missing because we couldn't see it. Ignorance was bliss, honestly. And the best nights of my life were never the ones everyone else was at.",
    category: 'digital',
    relatedModules: ['module-07'],
    mood: 'perspective'
  },
  {
    id: 'nana_007',
    text: "Group chats sound exhausting, frankly. In my day, if you wanted to coordinate three friends, you spent half the day on the telephone. Come to think of it, that was exhausting too. Friendships have always required effort.",
    category: 'digital',
    relatedModules: ['module-02'],
    mood: 'humorous'
  },
  {
    id: 'nana_008',
    text: "You can 'block' someone now? We had to move towns. Actually, that's not entirely a joke. Protecting your peace has always been valid. The tools just look different.",
    category: 'digital',
    relatedModules: ['module-05'],
    mood: 'validating'
  },
  {
    id: 'nana_009',
    text: "In my day, you didn't announce your friendships publicly. Now you tag them in posts. Both have pros and cons, I suppose. Just remember: the friendship is what matters, not the performance of it.",
    category: 'digital',
    relatedModules: ['module-04'],
    mood: 'wise'
  },
  {
    id: 'nana_010',
    text: "Being reachable 24/7 sounds like a nightmare, frankly. We had one telephone on the wall and answering it was optional. You're allowed to be unavailable sometimes. People managed for millennia without instant responses.",
    category: 'digital',
    relatedModules: ['module-04'],
    mood: 'reassuring'
  },

  // ===== CONFLICT & DRAMA (11-20) =====
  {
    id: 'nana_011',
    text: "Every meaningful friendship I've had involved at least one big argument. The ones that survived it became the strongest. Conflict isn't the end — it's often the beginning of something deeper.",
    category: 'conflict',
    relatedModules: ['module-03'],
    mood: 'reassuring'
  },
  {
    id: 'nana_012',
    text: "Friend groups picking sides in a conflict? Oh, we had that. The whole village knew everyone's business and took positions. Drama isn't a generational invention, love. It's deeply human.",
    category: 'conflict',
    relatedModules: ['module-02'],
    mood: 'normalising'
  },
  {
    id: 'nana_013',
    text: "I've apologised when I was right and stayed silent when I was wrong. Both felt terrible. Apologising for *your part* isn't admitting the other person was right. It's just taking responsibility for yourself. I wish I'd learned that earlier.",
    category: 'conflict',
    relatedModules: ['module-03'],
    mood: 'wise'
  },
  {
    id: 'nana_014',
    text: "I held a grudge against my friend Margaret for fifteen years over something I can't even remember now. What a waste. Forgiveness isn't about them — it's about not carrying that weight anymore.",
    category: 'conflict',
    relatedModules: ['module-03'],
    mood: 'reflective'
  },
  {
    id: 'nana_015',
    text: "I've lost friendships because I said difficult truths. I've also lost friendships because I didn't. At least when you're honest, you know you tried. Silence just leaves everyone guessing.",
    category: 'conflict',
    relatedModules: ['module-03'],
    mood: 'wise'
  },
  {
    id: 'nana_016',
    text: "We didn't call them 'friend breakups' but they happened. And they hurt just as much as romantic ones, sometimes more. Your grief is valid, even if there's no word for it.",
    category: 'conflict',
    relatedModules: ['module-05'],
    mood: 'validating'
  },
  {
    id: 'nana_017',
    text: "Dragging other friends into your falling-out? We called that 'triangulation' and it never ended well. Sort it out with them directly if you can. But talking it over with a grown-up you trust isn't taking sides, love, and if anything feels unsafe, tell one straight away.",
    category: 'conflict',
    relatedModules: ['module-03'],
    mood: 'wise'
  },
  {
    id: 'nana_018',
    text: "My mother always said 'never send a letter in anger.' Same applies to texts, I imagine. Write it, sleep on it, then decide if it still needs sending.",
    category: 'conflict',
    relatedModules: ['module-04'],
    mood: 'practical'
  },
  {
    id: 'nana_019',
    text: "The silent treatment was alive and well in my day too. It's still a terrible way to handle conflict. Use your words, even when they're hard. Silence just lets resentment grow.",
    category: 'conflict',
    relatedModules: ['module-03'],
    mood: 'direct'
  },
  {
    id: 'nana_020',
    text: "The best friendships aren't the ones without conflict — they're the ones that know how to repair. Every argument you survive together is proof the friendship can weather storms.",
    category: 'conflict',
    relatedModules: ['module-03'],
    mood: 'hopeful'
  },

  // ===== GROWING APART (21-30) =====
  {
    id: 'nana_021',
    text: "Not every friendship is meant to last forever, and that's alright. Some people are meant for a chapter, not the whole book. Honour what you had without forcing it to continue.",
    category: 'growing_apart',
    relatedModules: ['module-05'],
    mood: 'accepting'
  },
  {
    id: 'nana_022',
    text: "When my friends started having babies and I didn't, some friendships got harder. When I got sick and couldn't go out, others faded. Life changes us. Not everyone can travel the whole journey together.",
    category: 'growing_apart',
    relatedModules: ['module-05'],
    mood: 'understanding'
  },
  {
    id: 'nana_023',
    text: "My best friend from school became someone I barely recognised by thirty. It wasn't anyone's fault — we just grew in different directions. It's sad, but it's also just... life.",
    category: 'growing_apart',
    relatedModules: ['module-05'],
    mood: 'accepting'
  },
  {
    id: 'nana_024',
    text: "I had dozens of friends at twenty, a handful at forty, and could count the real ones on one hand by sixty. Quality over quantity isn't just a saying — it's how friendship naturally distils over time.",
    category: 'growing_apart',
    relatedModules: ['module-06'],
    mood: 'wise'
  },
  {
    id: 'nana_025',
    text: "Pay attention to who's there when things are hard. Not the big dramatic hard — the boring hard. The everyday hard. Those are your people.",
    category: 'growing_apart',
    relatedModules: ['module-06'],
    mood: 'wise'
  },
  {
    id: 'nana_026',
    text: "Sometimes the kindest thing is to let a friendship go gently. No big fight, no blame. Just... releasing your grip and seeing if it floats or sinks.",
    category: 'growing_apart',
    relatedModules: ['module-05'],
    mood: 'accepting'
  },
  {
    id: 'nana_027',
    text: "You can mourn a friend who's still alive. When someone changes, or you do, there's a real loss there. Let yourself grieve it.",
    category: 'growing_apart',
    relatedModules: ['module-05'],
    mood: 'validating'
  },
  {
    id: 'nana_028',
    text: "Some friends are for certain seasons only. Work friends, school friends, new-parent friends. When the season ends, so might the friendship. That doesn't mean it wasn't real.",
    category: 'growing_apart',
    relatedModules: ['module-05'],
    mood: 'accepting'
  },
  {
    id: 'nana_029',
    text: "Not every friendship ends with a bang. Most end with a whimper — fewer texts, longer gaps, until one day you realise it's been a year. It's sad, but it's normal.",
    category: 'growing_apart',
    relatedModules: ['module-05'],
    mood: 'normalising'
  },
  {
    id: 'nana_030',
    text: "The friends who knew you when you were young are precious. They remember who you were. But don't stay stuck in who you were just to keep them comfortable.",
    category: 'growing_apart',
    relatedModules: ['module-08'],
    mood: 'wise'
  },

  // ===== BOUNDARIES & SELF-WORTH (31-40) =====
  {
    id: 'nana_031',
    text: "I spent forty years making sure everyone else was comfortable while I was miserable. Don't be like me. Your needs matter just as much as everyone else's.",
    category: 'boundaries',
    relatedModules: ['module-03'],
    mood: 'direct'
  },
  {
    id: 'nana_032',
    text: "My generation was taught that 'no' was rude. What nonsense. 'No' is a complete sentence. Use it more than I did.",
    category: 'boundaries',
    relatedModules: ['module-03'],
    mood: 'empowering'
  },
  {
    id: 'nana_033',
    text: "You can be so nice that nobody knows who you really are. I was like that for years. It's lonely, being pleasant all the time. Let people see the real you — even the inconvenient parts.",
    category: 'boundaries',
    relatedModules: ['module-07'],
    mood: 'reflective'
  },
  {
    id: 'nana_034',
    text: "A friendship where one person always gives and one always takes isn't a friendship — it's an arrangement. Notice who shows up for you, not just who you show up for.",
    category: 'boundaries',
    relatedModules: ['module-02'],
    mood: 'wise'
  },
  {
    id: 'nana_035',
    text: "You don't need to earn friendship by being useful. You deserve connection just for being you. Took me seventy years to learn that. Learn it faster than I did.",
    category: 'boundaries',
    relatedModules: ['module-07'],
    mood: 'validating'
  },
  {
    id: 'nana_036',
    text: "Having boundaries doesn't make you cold. It makes you clear. The people who respect your limits are the ones worth keeping.",
    category: 'boundaries',
    relatedModules: ['module-03'],
    mood: 'empowering'
  },
  {
    id: 'nana_037',
    text: "In my day, we were told to put up with more than we should have. You don't have to tolerate poor treatment just because it's familiar. You can want better.",
    category: 'boundaries',
    relatedModules: ['module-05'],
    mood: 'empowering'
  },
  {
    id: 'nana_038',
    text: "My mother called it 'minding your own business.' Your generation calls it 'protecting your peace.' Same wisdom, different words. Do it.",
    category: 'boundaries',
    relatedModules: ['module-04'],
    mood: 'humorous'
  },
  {
    id: 'nana_039',
    text: "I spent decades hoping people would guess what I needed. Shocking news: they couldn't. Ask clearly. Good friends want to know how to love you well.",
    category: 'boundaries',
    relatedModules: ['module-03'],
    mood: 'practical'
  },
  {
    id: 'nana_040',
    text: "I used to abandon myself in friendships — ignoring my feelings, pretending I was fine. The friendships felt hollow anyway. You can't truly connect with others while betraying yourself.",
    category: 'boundaries',
    relatedModules: ['module-07'],
    mood: 'reflective'
  },

  // ===== TIMELESS TRUTHS (41-50) =====
  {
    id: 'nana_041',
    text: "Every generation thinks friendship is harder for them. You know what? They're all right. It's always been hard. You're not doing anything wrong.",
    category: 'timeless',
    relatedModules: ['module-01'],
    mood: 'normalising'
  },
  {
    id: 'nana_042',
    text: "Eighty percent of friendship is just showing up. Not perfectly, not with solutions. Just... present. That's more than most people manage.",
    category: 'timeless',
    relatedModules: ['module-06'],
    mood: 'reassuring'
  },
  {
    id: 'nana_043',
    text: "The 'golden age' of friendship everyone romanticises? They had drama too. Trust me. I was there.",
    category: 'timeless',
    relatedModules: ['module-02'],
    mood: 'humorous'
  },
  {
    id: 'nana_044',
    text: "If you feel like everyone has it figured out except you — they don't. Everyone's improvising. Everyone's uncertain. Some just hide it better.",
    category: 'timeless',
    relatedModules: ['module-07'],
    mood: 'normalising'
  },
  {
    id: 'nana_045',
    text: "It doesn't matter what you're doing with friends — it matters that you're doing it together. I had more meaningful moments washing dishes with my friend than at any fancy dinner.",
    category: 'timeless',
    relatedModules: ['module-06'],
    mood: 'wise'
  },
  {
    id: 'nana_046',
    text: "The friends who know my worst moments are the friends I'm closest to. There's something bonding about being truly seen, mess and all.",
    category: 'timeless',
    relatedModules: ['module-07'],
    mood: 'wise'
  },
  {
    id: 'nana_047',
    text: "Any fool can start a friendship. The real skill is in maintaining one. And the secret to that? Learning how to repair when things go wrong.",
    category: 'timeless',
    relatedModules: ['module-03'],
    mood: 'wise'
  },
  {
    id: 'nana_048',
    text: "Loneliness doesn't mean you're broken. It means you're human. Even in the most connected times, people have felt alone. Be gentle with yourself about it.",
    category: 'timeless',
    relatedModules: ['module-06'],
    mood: 'comforting'
  },
  {
    id: 'nana_049',
    text: "Friendships don't maintain themselves. They need watering like plants. Neglect them and they wither. Everyone wants a low-maintenance friendship, but the best ones require consistent care.",
    category: 'timeless',
    relatedModules: ['module-08'],
    mood: 'practical'
  },
  {
    id: 'nana_050',
    text: "Your people are out there. If you haven't found them yet, keep looking. I didn't meet my closest friend until I was forty-three. Good things take time sometimes.",
    category: 'timeless',
    relatedModules: ['module-06'],
    mood: 'hopeful'
  },

  // ===== COMFORT & ENCOURAGEMENT (51-60) =====
  {
    id: 'nana_051',
    text: "If someone says you're 'too much,' they're just not enough. The right friends will appreciate your intensity. Don't dim yourself for anyone.",
    category: 'encouragement',
    relatedModules: ['module-07'],
    mood: 'empowering'
  },
  {
    id: 'nana_052',
    text: "I've rebuilt my social life three times — after moving, after divorce, after illness. It's terrifying and exhausting. But it's also possible. You can start again at any age.",
    category: 'encouragement',
    relatedModules: ['module-06'],
    mood: 'hopeful'
  },
  {
    id: 'nana_053',
    text: "The skills you're learning now will become second nature eventually. Right now, it feels like hard work. One day it'll feel like breathing. Keep going.",
    category: 'encouragement',
    relatedModules: ['module-08'],
    mood: 'encouraging'
  },
  {
    id: 'nana_054',
    text: "Waiting for a perfect friendship is like waiting for a perfect day. It doesn't exist. Good enough is good enough.",
    category: 'encouragement',
    relatedModules: ['module-06'],
    mood: 'practical'
  },
  {
    id: 'nana_055',
    text: "Some people figure out friendship at fifteen. Others at fifty. There's no deadline. You're not behind — you're just on your own timeline.",
    category: 'encouragement',
    relatedModules: ['module-01'],
    mood: 'reassuring'
  },
  {
    id: 'nana_056',
    text: "Everyone goes through friendship droughts. Times when you feel disconnected from everyone. It's a phase, not a life sentence. It will pass.",
    category: 'encouragement',
    relatedModules: ['module-06'],
    mood: 'hopeful'
  },
  {
    id: 'nana_057',
    text: "After a painful friendship ending, trying again feels impossible. I know. But isolation is worse. When you're ready — and only when you're ready — give people another chance.",
    category: 'encouragement',
    relatedModules: ['module-05'],
    mood: 'gentle'
  },
  {
    id: 'nana_058',
    text: "You don't need to transform overnight. One small connection, one honest conversation, one boundary set — that's enough progress for today.",
    category: 'encouragement',
    relatedModules: ['module-08'],
    mood: 'gentle'
  },
  {
    id: 'nana_059',
    text: "The fact that you're here, working on this, learning about yourself and your friendships? That takes courage. I'm proud of you. Really.",
    category: 'encouragement',
    relatedModules: ['module-01'],
    mood: 'proud'
  },
  {
    id: 'nana_060',
    text: "Whatever you're feeling right now about your friendships — someone else has felt it too. You're not weird, you're not broken, you're not alone. This is the human experience.",
    category: 'encouragement',
    relatedModules: ['module-01'],
    mood: 'comforting'
  }
];

/**
 * Get a random wisdom that hasn't been seen yet
 */
export const getUnseenWisdom = (seenIds = []) => {
  const unseen = NANA_WISDOM.filter(w => !seenIds.includes(w.id));
  if (unseen.length === 0) return null;
  return unseen[Math.floor(Math.random() * unseen.length)];
};

/**
 * Get wisdom relevant to a specific module
 */
export const getWisdomForModule = (moduleId, seenIds = []) => {
  const relevant = NANA_WISDOM.filter(
    w => w.relatedModules.includes(moduleId) && !seenIds.includes(w.id)
  );
  if (relevant.length === 0) return getUnseenWisdom(seenIds);
  return relevant[Math.floor(Math.random() * relevant.length)];
};

/**
 * Get a random Nana variant
 */
export const getRandomNanaVariant = () => {
  return NANA_VARIANTS[Math.floor(Math.random() * NANA_VARIANTS.length)];
};

export default NANA_WISDOM;
